// "My default" (the Worklist and History, beside Columns): the person's saved view of the page — its
// filters as the address keeps them and its sort — applied on every fresh open on any computer
// (`openingViewOf`). Saved only from this menu: never by Ask AI, a link or Back. A `user-preference`
// record, the person's own (the server refuses anyone else's), never in the System log.
// OUTSIDE-PLATFORM: P20 the platform has no store for an app's per-person settings.

import { useState } from "react";
import { BookmarkIcon } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { USER_PREFERENCES, formatDay } from "@app/shared";
import type { Mode, MyViewPayload } from "@app/shared";
import type { SortingState } from "@tanstack/react-table";
import { Popover, PopoverContent, PopoverTrigger, ToolbarButton, crudApi, crudErrorText } from "@axny/platform-ui";
import { MY_VIEW_KEY } from "./data";
import { useLoggedWrites } from "./logged-write";
import { notify, useMessage } from "./message";
import { sortText } from "./saved-sort";

/** A saved default that says something: a view or a sort. */
export const hasDefault = (p: { view?: string; sort?: string } | undefined) => !!(p?.view || p?.sort);

export function MyDefaultMenu({
  mode,
  my,
  address,
  sorting,
  hasMine,
  onResetToMine,
}: {
  mode: Mode;
  my: MyViewPayload | null;
  /** The view as the address keeps it now (`viewToAddress`). */
  address: Record<string, string | string[]>;
  sorting: SortingState;
  /** The person has customers: "Reset to my customers" is offered. */
  hasMine: boolean;
  onResetToMine: () => void;
}) {
  const writes = useLoggedWrites();
  const message = useMessage();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const saved = my?.preferences[mode];
  const isSet = hasDefault(saved);

  /** Writes this page's record of the person: created once, then updated in place. */
  const write = async (view: string, sort: string, done: string) => {
    if (!my) return;
    setBusy(true);
    try {
      const input = { personEmail: my.email, page: mode, view, sort, updatedAt: new Date().toISOString() };
      const log = { own: "saved view" };
      if (saved) {
        // Its version now: another tab of the person's may have saved it since.
        const now = await crudApi.get(USER_PREFERENCES.id, saved.id);
        await writes.update(USER_PREFERENCES, saved.id, String(now.rv), input, log);
      } else await writes.create(USER_PREFERENCES, input, log);
      notify(done, "It applies whenever you open this page, on any computer.");
      setOpen(false);
    } catch (e) {
      await message.error("Couldn't save your default", crudErrorText(e));
    } finally {
      setBusy(false);
      await queryClient.invalidateQueries({ queryKey: MY_VIEW_KEY });
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <ToolbarButton
            icon={<BookmarkIcon />}
            label="My default"
            hint={
              isSet
                ? `Your saved view of this page${saved?.updatedAt ? ` (${formatDay(saved.updatedAt.slice(0, 10))})` : ""}`
                : "Save this view as the one this page opens with"
            }
            variant={isSet ? "secondary" : "outline"}
            data-testid="my-default"
          />
        }
      />
      <PopoverContent align="end" className="w-72 gap-1 p-1.5 text-[13px]">
        <button
          type="button"
          className="hover:bg-muted rounded px-2 py-1.5 text-left disabled:opacity-50"
          disabled={busy || !my}
          onClick={() => void write(JSON.stringify(address), sortText(sorting), "Saved as your default")}
        >
          Save current view as my default
          <span className="text-muted-foreground block text-xs">The filters and the sort, as they are now</span>
        </button>
        {hasMine ? (
          <button
            type="button"
            className="hover:bg-muted rounded px-2 py-1.5 text-left"
            disabled={busy}
            onClick={() => {
              onResetToMine();
              setOpen(false);
            }}
          >
            Reset to my customers
            <span className="text-muted-foreground block text-xs">Show your customers now; your default stays</span>
          </button>
        ) : null}
        {isSet ? (
          <button
            type="button"
            className="hover:bg-muted rounded px-2 py-1.5 text-left disabled:opacity-50"
            disabled={busy}
            onClick={() => void write("", "", "Your default is removed")}
          >
            Remove my default
            <span className="text-muted-foreground block text-xs">
              {hasMine ? "This page opens on your customers again" : "This page opens as it does for everyone"}
            </span>
          </button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
